import React, {useMemo, useState, useCallback} from 'react';
import ModelerX6 from '@core_x6/lib/ModelerX6';
import EntryIcon from './EntryIcon';
import RelationshipTypeIcon from './RelationshipTypeIcon';
import RubberbandIcon from './RubberbandIcon';

const TOOLS_GROUP_KEY = '__tools__';

/**
 * Палитра элементов ArchiMate и типов связей (замена Stencil).
 * — Секция «Связи»: кнопки типов связей (выбор для будущего магнита).
 * — Группы элементов: перетаскивание на канву через Dnd.start (modeler.startDragFromPalette).
 * — Инструменты: рамка выделения / панорамирование.
 */
export default function ArchiX6Palette({
	modeler,
	className = '',
	style = {},
	selectionMode = false,
	onToggleSelectionMode,
}) {
	const [selectedRelType, setSelectedRelType] = useState(null);
	const [collapsedGroups, setCollapsedGroups] = useState(() => ({}));

	const groups = useMemo(() => ModelerX6.getPaletteGroups(), []);
	const connectionTypes = useMemo(() => ModelerX6.CONNECTION_TYPE_OPTIONS ?? [], []);

	const handleRelationshipClick = useCallback(
		type => {
			const next = selectedRelType === type ? null : type;
			setSelectedRelType(next);
			modeler?.setSelectedRelationshipType?.(next);
		},
		[modeler, selectedRelType]
	);

	const handlePaletteEntryMouseDown = useCallback(
		(e, entry) => {
			if (!modeler?.startDragFromPalette) return;
			e.preventDefault();
			modeler.startDragFromPalette(
				entry.type,
				{
					typeName: entry.typeName,
					width: entry.width ?? 60,
					height: entry.height ?? 32,
				},
				e.nativeEvent
			);
		},
		[modeler]
	);

	const toggleGroup = useCallback(name => {
		setCollapsedGroups(prev => ({...prev, [name]: !prev[name]}));
	}, []);

	if (!modeler) return null;

	return (
		<div
			className={`archi-x6-palette ${className}`.trim()}
			style={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
				height: '100%',
				overflow: 'hidden',
				...style,
			}}
		>
			{/* Связи (пока скрыто) */}
			<div
				style={{
					display: 'none',
					flexShrink: 0,
					padding: '8px 8px 6px',
					borderBottom: '1px solid #e0e0e0',
					background: '#fafafa',
				}}
			>
				<div
					style={{
						fontWeight: 600,
						fontSize: 12,
						marginBottom: 6,
						color: '#333',
					}}
				>
					Связи
				</div>
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: 4,
					}}
				>
					{connectionTypes.map(({type, label}) => (
						<button
							key={type}
							type="button"
							title={label}
							onClick={() => handleRelationshipClick(type)}
							className="archi-x6-palette-rel-btn"
							data-selected={selectedRelType === type}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: 32,
								height: 32,
								minWidth: 32,
								minHeight: 32,
								padding: 0,
								cursor: 'pointer',
								border: 'none',
								outline: 'none',
								borderRadius: 4,
								background:
									selectedRelType === type
										? 'rgba(33, 150, 243, 0.12)'
										: 'transparent',
							}}
						>
							<span
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
									height: '100%',
								}}
							>
								<RelationshipTypeIcon type={type} size={24} />
							</span>
						</button>
					))}
				</div>
			</div>

			{/* Группы элементов + Инструменты */}
			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflow: 'auto',
					padding: '4px 0',
				}}
			>
				{typeof onToggleSelectionMode === 'function' && (
					<div style={{marginBottom: 4}}>
						<button
							type="button"
							onClick={() => toggleGroup(TOOLS_GROUP_KEY)}
							style={{
								width: '100%',
								textAlign: 'left',
								padding: '4px 8px',
								fontSize: 11,
								fontWeight: 600,
								border: 'none',
								background: '#eee',
								cursor: 'pointer',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
							}}
						>
							<span>Инструменты</span>
							<span style={{fontSize: 10}}>
								{collapsedGroups[TOOLS_GROUP_KEY] ? '▶' : '▼'}
							</span>
						</button>
						{!collapsedGroups[TOOLS_GROUP_KEY] && (
							<div
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 4,
									padding: '4px 8px 8px',
									background: '#fff',
								}}
							>
								<button
									type="button"
									className="archi-x6-palette-entry-btn"
									title={
										selectionMode
											? 'Выключить выделение (двигать доску)'
											: 'Включить выделение (рамка)'
									}
									onClick={onToggleSelectionMode}
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: 32,
										height: 32,
										minWidth: 32,
										minHeight: 32,
										padding: 0,
										cursor: 'pointer',
										border: 'none',
										outline: 'none',
										borderRadius: 4,
										background: selectionMode
											? 'rgba(24, 144, 255, 0.12)'
											: 'transparent',
										color: selectionMode ? '#1890ff' : 'rgba(0,0,0,0.65)',
									}}
								>
									<RubberbandIcon size={18} />
								</button>
							</div>
						)}
					</div>
				)}
				{groups.map(group => {
					const isCollapsed = collapsedGroups[group.name];
					return (
						<div
							key={group.name}
							style={{
								marginBottom: 4,
							}}
						>
							<button
								type="button"
								onClick={() => toggleGroup(group.name)}
								style={{
									width: '100%',
									textAlign: 'left',
									padding: '4px 8px',
									fontSize: 11,
									fontWeight: 600,
									border: 'none',
									background: '#eee',
									cursor: 'pointer',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<span>{group.title}</span>
								<span style={{fontSize: 10}}>{isCollapsed ? '▶' : '▼'}</span>
							</button>
							{!isCollapsed && (
								<div
									style={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: 4,
										padding: '4px 8px 8px',
										background: '#fff',
									}}
								>
									{group.entries.map(entry => (
										<button
											key={`${group.name}-${entry.type}-${entry.typeName}`}
											type="button"
											className="archi-x6-palette-entry-btn"
											title={entry.typeName}
											onMouseDown={e => handlePaletteEntryMouseDown(e, entry)}
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												width: 32,
												height: 32,
												minWidth: 32,
												minHeight: 32,
												padding: 0,
												cursor: 'grab',
												border: 'none',
												outline: 'none',
												borderRadius: 4,
												background: 'transparent',
											}}
										>
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: '100%',
													height: '100%',
												}}
											>
												<EntryIcon entry={entry} modeler={modeler} />
											</span>
										</button>
									))}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
